import { __stringify, BadImplementationException, exists } from '@nu-art/ts-common';
import { getReturnValueType } from './evaluation-logics/generic-conversion.js';
// import {AtomVarWithEvalLogic, SubTermsConversion} from '../../expression-schema-types/ClinicalFunctionExpression.js';
import { getEvaluationLogicType } from './convertAtomLevel.js';
import { EvaluationLogicTypes } from './consts.js';
import { resolveInSet } from './evaluation-logics/set.js';
import { resolveRangeEvaluationLogic } from './evaluation-logics/range.js';
import { resolveNotEqual } from './evaluation-logics/not-equal.js';
import { resolveExists } from './evaluation-logics/exists.js';
import { resolveNotExists } from './evaluation-logics/not-exists.js';
import { LogicType_Add, LogicType_GreaterOrEqual, LogicType_Self, LogicType_StructuredFreeText } from '../../../_enum.js';
/**
 * Converts an array of terms to their corresponding AI team schema representation within the converted expression.
 * @param terms - An array of database terms to be converted.
 * @param convertedExpression - The converted expression to which the terms will be added.
 * @param dependencies - db dependencies mapper
 */
export const convertTermLevel = (terms, convertedExpression, dependencies) => {
    convertedExpression.terms = terms.reduce((convertedTerms, currentTerm, currentIndex) => {
        const schemaTerm = {};
        schemaTerm.atoms = {};
        // Add comment if it exists
        if (exists(currentTerm.comment))
            schemaTerm.comment = currentTerm.comment;
        // Set term weight to null because of no actual example of a usage
        schemaTerm.term_weight = null;
        schemaTerm.evaluation_logic = composeEvaluationLogic(currentTerm, dependencies);
        convertedTerms[`T${currentIndex + 1}`] = schemaTerm;
        return convertedTerms;
    }, convertedExpression.terms);
};
const isTermWithSubTerms = (term) => term.subTerms !== undefined;
/**
 * Composes the evaluation logic for a term.
 * @param dbTerm - The database term to be converted.
 * @param dependencies - Dependencies mapper
 * @returns The basic expression representing the evaluation logic of the term.
 */
const composeEvaluationLogic = (dbTerm, dependencies) => {
    if (isTermWithSubTerms(dbTerm))
        return resolveSubTermConversion(dbTerm, dependencies);
    if (dbTerm.atomsLogic === LogicType_GreaterOrEqual)
        return resolveAtLeastExpression(dbTerm);
    if (dbTerm.value || dbTerm.atomsLogic === LogicType_StructuredFreeText)
        return resolveSimpleExpressionConversion(dbTerm);
    return resolveSimpleExpression(dbTerm);
};
const resolveSimpleExpressionOutputType = (dbTerm) => {
    let outputType = 'boolean';
    if (dbTerm.atomsLogic === LogicType_Add)
        outputType = 'number';
    const firstAtom = dbTerm.atoms[0];
    if (dbTerm.atoms.length === 1 && firstAtom.cases.every(_case => exists(_case.value))) {
        if (firstAtom.cases.every(_case => getReturnValueType(_case.value) === 'number'))
            outputType = 'number';
        if (firstAtom.cases.every(_case => getReturnValueType(_case.value) === 'tuple'))
            outputType = 'tuple';
    }
    const firstCase = firstAtom.cases[0];
    if (dbTerm.atoms.length === 1 && firstCase.caseOperator === LogicType_Self) {
        if (firstCase.valueType === 'boolean' || firstCase.valueType === 'enum')
            outputType = 'string';
        if (firstCase.valueType === 'numeric')
            outputType = 'number';
    }
    return outputType;
};
const resolveSimpleExpression = (dbTerm) => {
    const simpleExpr = {};
    simpleExpr.type = 'simple-expression';
    simpleExpr.output_type = resolveSimpleExpressionOutputType(dbTerm);
    simpleExpr.expression = dbTerm.expression;
    return simpleExpr;
};
const resolveSimpleExpressionConversion = (dbTerm) => {
    const simpleExprConversion = {};
    simpleExprConversion.type = 'simple-expression-conversion';
    // Handle free structured text conversion
    if (dbTerm.atomsLogic === LogicType_StructuredFreeText) {
        if (!dbTerm.structuredFreeText?.if || !dbTerm.structuredFreeText?.then || !dbTerm.structuredFreeText?.else)
            throw new BadImplementationException('cannot convert to simple expression conversion with missing values');
        simpleExprConversion.expression = dbTerm.structuredFreeText?.if;
        simpleExprConversion.return_value = dbTerm.structuredFreeText?.then;
        simpleExprConversion.else_value = dbTerm.structuredFreeText?.else;
        switch (dbTerm.outputType) {
            case 'numeric':
                simpleExprConversion.output_type = 'number';
                break;
            case 'range':
                simpleExprConversion.output_type = 'tuple';
                break;
            default:
                simpleExprConversion.output_type = dbTerm.outputType;
                break;
        }
    }
    else {
        // Handle term value conversion
        simpleExprConversion.expression = dbTerm.expression;
        simpleExprConversion.output_type = getReturnValueType(dbTerm.value);
        simpleExprConversion.return_value = getReturnValueType(dbTerm.value) === 'tuple' ? dbTerm.value : dbTerm.value[0];
        simpleExprConversion.else_value = getReturnValueType(dbTerm.fallbackValue) === 'tuple' ? dbTerm.fallbackValue : dbTerm.fallbackValue[0];
    }
    return simpleExprConversion;
};
const resolveSubTermConversion = (dbTerm, dependencies) => {
    const subTermConversion = {};
    subTermConversion.type = 'sub-terms-conversion';
    const isTuple = dbTerm.subTerms.every(subTerm => getReturnValueType(subTerm.value) === 'tuple');
    subTermConversion.output_type = isTuple ? 'tuple' : 'number';
    subTermConversion.else_value = isTuple ? dbTerm.fallbackValue : dbTerm.fallbackValue[0];
    subTermConversion.options = resolveSubTermOptions(dbTerm.subTerms, dependencies);
    return subTermConversion;
};
const resolveSubTermOptions = (subTerms, dependencies) => {
    return subTerms.map(dbSubTerm => {
        // Create basic meta data for schema sub term
        const schemaSubTerm = {
            type: 'sub-term',
            return_value: getReturnValueType(dbSubTerm.value) === 'tuple' ? dbSubTerm.value : dbSubTerm.value[0]
        };
        //
        schemaSubTerm.atoms_conditions = dbSubTerm.atoms.map(atom => {
            if (!atom.leftHand)
                throw new BadImplementationException('cannot use empty atom in expression');
            const convertedSubTerm = { atom_var: `A${atom.leftHand?.id}` };
            convertedSubTerm.evaluation_logic = resolveAtomWithVarEvalLogic(atom, dependencies);
            if (atom) {
                return convertedSubTerm;
            }
        });
        return schemaSubTerm;
    });
};
const resolveAtomWithVarEvalLogic = (dbAtom, dependencies) => {
    const evaluationLogicType = getEvaluationLogicType(dbAtom, dependencies);
    switch (evaluationLogicType) {
        case EvaluationLogicTypes.set:
            return resolveInSet(dbAtom, dependencies);
        case EvaluationLogicTypes.range:
            return resolveRangeEvaluationLogic(dbAtom, dependencies);
        case EvaluationLogicTypes.notEqual:
            return resolveNotEqual(dbAtom, dependencies);
        case EvaluationLogicTypes.exists:
            return resolveExists();
        case EvaluationLogicTypes.notExists:
            return resolveNotExists();
    }
    throw new BadImplementationException(`could not resolve eval logic for db atom ${__stringify(dbAtom, true)}`);
};
const resolveAtLeastExpression = (term) => {
    const toNumber = (v) => Array.isArray(v) ? v[0] : v;
    return {
        type: 'at-least',
        output_type: 'boolean',
        threshold: toNumber(term.value),
    };
};
//# sourceMappingURL=convertTermLevel.js.map